<template>
  <div>
    <p>The Admin page has two tabs:</p>

    <ul>
      <li>
        <b>Participants:</b> Here is where you can see all students who have
        unlocked achievements
      </li>
      <li>
        <b>Content:</b> Here is a table of links to glossaries that run this
        app.
      </li>
    </ul>

    <div class="mb-4">
      <div class="h3 fw-bold mb-3">Achievements Glossary</div>
      <p>
        Achievements are created using the appropriate Achievements glossary.
      </p>

      <p>
        The
        <b>All Achievements</b> tab in the navigation will allow you to view all
        achievements. Those that are unapproved (hide) will not be shown in this
        list.
      </p>

      <p>
        The
        <b>My Achievements</b> tab in the navigation will allow you to add,
        edit, and unapprove (hide) achievements.
      </p>

      <div class="fs-5 fw-bold mb-1">Achievements Form</div>

      <p>
        Each achievement has the following fields that must be filled out to
        create a valid achievement.
      </p>

      <div class="fw-bold mb-1">Achievement Name</div>
      <p>
        This is the name of the achievement and is a required field. The name
        should be unique and descriptive. We recommend the name not be too long,
        so it fits nicely on the achievement badge.
      </p>

      <div class="fw-bold mb-1">Definition</div>
      <p>
        This is a brief description of the achievement and is a required field.
        The definition should explain what the achievement is and how to earn
        it. We recommend keeping the definition concise, so it fits nicely on
        the achievement badge.
      </p>

      <div class="fw-bold mb-1">Points</div>
      <p>
        This is the number of points the achievement is worth and is a required
        field. Points must be a positive integer.
      </p>

      <div class="fw-bold mb-1">Target Score (0-5)</div>
      <p>
        This is the number of questions a student must answer correctly to earn
        the achievement and is a required field. The target score must be
        between 0 and 5, inclusive. Target score has a maximum of 5 because
        there can only be 5 questions per achievement.
      </p>

      <p>
        For example, if the target score is 3, the student must answer 3 out of
        5 questions correctly to earn the achievement.
      </p>

      <p>
        If the target score is 0, the student will earn the achievement just by
        visiting the achievement link.
      </p>

      <div class="fw-bold mb-1">Number of codes</div>
      <p>
        This is the number of unique codes that will be generated for this
        achievement. This is a required field.
      </p>

      <p>
        For example, if the number of codes is 10, then 10 unique codes will be
        generated for this achievement and will be visible on the achievement
        itself once you submit it in the glossary.
      </p>

      <div class="fw-bold mb-1">Notes</div>
      <p>
        This is an optional field where you can add any notes about the
        achievement. Such as where to find it in your Canvas course.
      </p>

      <div class="fw-bold mb-1">Category</div>
      <p>
        All achievements must belong to a category and this is a required field.
        Category is how we determine which questions to pool from which
        category.
      </p>

      <p>
        For example, if the category is "Math", then questions will be pulled
        from the "Math" question pool for this achievement.
      </p>

      <div class="fw-bold mb-1">Image</div>
      <p>
        This is an optional field where you can upload an image/icon that will
        be used in the achievement badge. If no image is provided, the
        achievement badge image slot will be empty.
      </p>

      <div class="fs-5 fw-bold mb-1">Hidden Achievements</div>
      <p>
        To hide achievements from students, you can select the
        <b>Unapprove</b> button. This will hide it from the achievements list on
        the app.
      </p>

      <p>
        Hidden achievements can still be earned if a student has the direct link
        to the achievement. This is useful for achievements that are only meant
        for certain students or groups. You could also use this feature to
        create easter eggs or special achievements that are not listed in the
        main achievements list.
      </p>
    </div>

    <div class="mb-4">
      <div class="h3 fw-bold mb-3">Questions Glossary</div>

      <p>Questions are created using the appropriate Questions glossary.</p>

      <p>
        The <b>All Questions</b> tab in the navigation will allow you to view
        all questions.
      </p>

      <p>
        The
        <b>My Questions</b> tab in the navigation will allow you to add, edit,
        and unapprove (hide) questions. Note that unapproving (hiding) a
        question removes it from the pool of questions that can be used.
      </p>

      <p>All questions are multiple choice with one correct answer.</p>

      <div class="fs-5 fw-bold mb-1">Question Form</div>

      <p>
        Each question has the following fields that must be filled out to create
        a valid question.
      </p>

      <div class="fw-bold mb-1">Question</div>
      <p>
        This is the text of the question and is a required field. The question
        should be clear and concise.
      </p>

      <p>
        You can also do multiple choice fill in-the-blank questions by using
        underscores. For example, "The capital of France is _______."
      </p>

      <div class="fw-bold mb-1">Answers</div>
      <p>
        There must be between 2 and 4 answers (inclusive) for each question.
      </p>

      <p><b>Answer 1</b> is always the correct answer.</p>

      <p>
        You can also do true/false questions by using "True" and "False" as
        answers.
      </p>

      <div class="fw-bold mb-1">Category</div>
      <p>
        All questions must belong to a category and this is a required field.
        Category is how we determine which questions to pool from which category
        for each achievement.
      </p>
    </div>

    <div class="mb-4">
      <div class="h3 fw-bold mb-3">Award Points Glossary</div>

      <p>
        As an instructor, you can manually award points to groups using the
        Award Points glossary.
      </p>

      <p>
        The
        <b>All Awarded Points</b> tab in the navigation will allow you to view
        all points you've awarded.
      </p>

      <p>
        The
        <b>My Awarded Points</b> tab in the navigation will allow you to add and
        edit points you've awarded.
      </p>

      <div class="fs-5 fw-bold mb-1">Award Points Form</div>

      <p>
        Each award points entry has the following fields that must be filled out
        to create a valid entry.
      </p>

      <div class="fw-bold mb-1">Group Name</div>

      <p>
        This is the name of the group you want to award points to and is a
        required field. The group name must match exactly how it appears in your
        Canvas course.
      </p>

      <div class="fw-bold mb-1">Points</div>

      <p>
        This is the number of points you want to award to the group and is a
        required field. Points must be a positive integer.
      </p>

      <div class="fw-bold mb-1">Reason</div>

      <p>
        This is a brief description of why you are awarding points to the group
        and is a required field. The reason should be clear and concise.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: "AdminFAQ"
};
</script>

<style scoped>
.h3 {
  border-bottom: 2px solid #ccc;
  padding-bottom: 0.25rem;
}
</style>
